import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  ConditionsPaiement,
  EcheancePriorite,
  EcheanceRow,
  EcheanceSens,
  EcheanceStatut,
  ModeReglement,
  TypeTierFournisseur,
} from '@/lib/database.types';

export interface EcheancesFilter {
  entrepriseId?: string | null;
  sens?: EcheanceSens | null;
  statut?: EcheanceStatut | null;
  tierId?: string | null;
  activiteId?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  amountMin?: number | null;
  amountMax?: number | null;
  search?: string;
  planPaiementId?: string | null;
  page?: number;
  pageSize?: number;
}

export function useEcheances(filter: EcheancesFilter) {
  const pageSize = filter.pageSize ?? 100;
  const page = filter.page ?? 0;
  return useQuery({
    queryKey: ['echeances', filter],
    queryFn: async (): Promise<{ rows: EcheanceRow[]; count: number }> => {
      let q = supabase
        .from('echeances')
        .select('*', { count: 'exact' })
        .order('date_echeance', { ascending: true })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.sens) q = q.eq('sens', filter.sens);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.tierId) q = q.eq('tier_id', filter.tierId);
      if (filter.activiteId) q = q.eq('activite_id', filter.activiteId);
      if (filter.planPaiementId) q = q.eq('plan_paiement_id', filter.planPaiementId);
      if (filter.dateFrom) q = q.gte('date_echeance', filter.dateFrom);
      if (filter.dateTo) q = q.lte('date_echeance', filter.dateTo);
      if (filter.amountMin !== null && filter.amountMin !== undefined)
        q = q.gte('montant_initial_dzd', filter.amountMin);
      if (filter.amountMax !== null && filter.amountMax !== undefined)
        q = q.lte('montant_initial_dzd', filter.amountMax);
      if (filter.search && filter.search.length >= 2) {
        const s = filter.search.replace(/%/g, '');
        q = q.or(`reference.ilike.%${s}%,facture_ref.ilike.%${s}%,motif.ilike.%${s}%`);
      }
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: data, count: count ?? 0 };
    },
    placeholderData: (prev) => prev,
  });
}

export interface UpsertEcheanceInput {
  id?: string;
  entreprise_id: string;
  sens: EcheanceSens;
  tier_id: string;
  activite_id?: string | null;
  service_id?: string | null;
  type_tier_fournisseur?: TypeTierFournisseur | null;
  facture_ref?: string | null;
  devis_ref?: string | null;
  bon_commande_ref?: string | null;
  date_emission?: string;
  date_echeance: string;
  date_reception_facture?: string | null;
  devise_code?: string;
  taux_change?: number;
  montant_initial: number;
  montant_paye?: number;
  montant_initial_dzd: number;
  montant_ht?: number | null;
  tva_taux?: number;
  montant_ttc?: number | null;
  acompte?: number;
  ras_taux?: number;
  net_a_payer?: number | null;
  conditions_paiement?: ConditionsPaiement | null;
  mode_reglement?: ModeReglement | null;
  priorite?: EcheancePriorite;
  justificatif_url?: string | null;
  commercial_id?: string | null;
  approbateur_id?: string | null;
  approbateur_required?: boolean;
  compte_scf_id?: string | null;
  centre_cout_activite_id?: string | null;
  motif?: string | null;
  observation?: string | null;
  plan_paiement_id?: string | null;
  tranche_numero?: number | null;
  statut?: EcheanceStatut;
}

export function useUpsertEcheance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertEcheanceInput): Promise<EcheanceRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('echeances')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase.from('echeances').insert(rest).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['echeances'] });
      void qc.invalidateQueries({ queryKey: ['cashflow'] });
      void qc.invalidateQueries({ queryKey: ['top_retards'] });
    },
  });
}

export function useDeleteEcheance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('echeances').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['echeances'] });
    },
  });
}

export function useUpdateEcheanceStatut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; statut: EcheanceStatut }): Promise<void> => {
      const { error } = await supabase
        .from('echeances')
        .update({ statut: input.statut })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['echeances'] });
    },
  });
}

export function useEnregistrerPaiement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; montant_paye: number }): Promise<void> => {
      const { error } = await supabase
        .from('echeances')
        .update({ montant_paye: input.montant_paye })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['echeances'] });
      void qc.invalidateQueries({ queryKey: ['cashflow'] });
    },
  });
}

export interface BulkResult {
  success: string[];
  errors: { id: string; message: string }[];
}

export function useBulkUpdateEcheanceStatut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; statut: EcheanceStatut }): Promise<BulkResult> => {
      const result: BulkResult = { success: [], errors: [] };
      const chunks: string[][] = [];
      for (let i = 0; i < input.ids.length; i += 200) chunks.push(input.ids.slice(i, i + 200));
      for (const chunk of chunks) {
        const { error, data } = await supabase
          .from('echeances')
          .update({ statut: input.statut })
          .in('id', chunk)
          .select('id');
        if (error) {
          for (const id of chunk) result.errors.push({ id, message: error.message });
        } else {
          for (const r of data ?? []) result.success.push(r.id);
        }
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['echeances'] });
    },
  });
}

export function useBulkDeleteEcheances() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkResult> => {
      const result: BulkResult = { success: [], errors: [] };
      const { error, data } = await supabase.from('echeances').delete().in('id', ids).select('id');
      if (error) {
        for (const id of ids) result.errors.push({ id, message: error.message });
      } else {
        for (const r of data ?? []) result.success.push(r.id);
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['echeances'] });
    },
  });
}

/**
 * KPIs agrégés en local depuis le dataset filtré (pas de RPC dédiée).
 */
export interface EcheanceKpis {
  totalDu: number;
  totalEnRetard: number;
  totalDepasse30: number;
  topTierId: string | null;
  topTierAmount: number;
  nbOuvertes: number;
  nbEnRetard: number;
}

export function computeKpis(rows: EcheanceRow[]): EcheanceKpis {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let totalDu = 0;
  let totalEnRetard = 0;
  let totalDepasse30 = 0;
  let nbOuvertes = 0;
  let nbEnRetard = 0;
  const perTier: Record<string, number> = {};
  for (const r of rows) {
    if (r.statut === 'payee' || r.statut === 'annulee') continue;
    const due = Number(r.montant_du);
    totalDu += due;
    nbOuvertes += 1;
    const ec = new Date(r.date_echeance);
    const diff = Math.floor((today.getTime() - ec.getTime()) / 86400000);
    if (diff > 0) {
      totalEnRetard += due;
      nbEnRetard += 1;
      if (diff > 30) totalDepasse30 += due;
    }
    perTier[r.tier_id] = (perTier[r.tier_id] ?? 0) + due;
  }
  let topTierId: string | null = null;
  let topTierAmount = 0;
  for (const [tier, amt] of Object.entries(perTier)) {
    if (amt > topTierAmount) {
      topTierId = tier;
      topTierAmount = amt;
    }
  }
  return {
    totalDu,
    totalEnRetard,
    totalDepasse30,
    topTierId,
    topTierAmount,
    nbOuvertes,
    nbEnRetard,
  };
}
